import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { waLink, IMG } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Kicker, Reveal } from "./Reveal";
import { WhatsAppIcon } from "./Icons";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const EMPTY = { first_name: "", last_name: "", phone: "", email: "", goal: "", preferred_time: "", message: "", consent: false, website: "" };
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldCls =
  "h-12 rounded-none border-2 border-ink bg-bone text-ink placeholder:text-ink/40 focus-visible:ring-0 focus-visible:border-hammer-red shadow-none text-base";

export const TrialForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t.trial.errRequired;
    if (form.phone.replace(/\D/g, "").length < 6) e.phone = t.trial.errRequired;
    if (form.email.trim() && !EMAIL_RX.test(form.email.trim())) e.email = t.trial.errEmail;
    if (!form.consent) e.consent = t.trial.errConsent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const goalLabel = form.goal ? t.trial.goals[form.goal] : "";
      const timeLabel = form.preferred_time ? t.trial.times[form.preferred_time] : "";
      await axios.post(`${API}/trial`, { ...form, goal: goalLabel, preferred_time: timeLabel, language: lang });
      setDone(true);
      setForm(EMPTY);
      setTimeout(() => document.getElementById("prova")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      toast.success(t.trial.toastOk);
    } catch (err) {
      const msg = err?.response?.status === 429 ? err.response.data?.detail : t.trial.errGeneric;
      toast.error(msg || t.trial.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  const Err = ({ k }) =>
    errors[k] ? (
      <p className="text-xs font-mono text-hammer-red mt-1.5" data-testid={`free-trial-error-${k}`}>
        {errors[k]}
      </p>
    ) : null;

  return (
    <section id="prova" data-testid="trial-section" className="relative paper text-ink overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 grid lg:grid-cols-12 gap-14">
        {/* left */}
        <div className="lg:col-span-5">
          <Reveal>
            <Kicker testid="trial-kicker">{t.trial.kicker}</Kicker>
            <h2 data-testid="trial-title" className="font-display text-6xl sm:text-7xl mt-5 leading-[0.86]">
              <span className="scribble-underline">{t.trial.title}</span>
            </h2>
            <p className="mt-6 text-ink/75 leading-relaxed max-w-[46ch]">{t.trial.sub}</p>
          </Reveal>

          <Reveal delay={0.1} className="mt-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60 mb-4">{t.trial.stepsTitle}</p>
            <ol className="space-y-4">
              {t.trial.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="w-10 h-10 shrink-0 grid place-items-center bg-ink text-bone font-display text-2xl pt-0.5">{i + 1}</span>
                  <span className="pt-2 font-medium">{s}</span>
                </li>
              ))}
            </ol>
          </Reveal>

          <Reveal delay={0.15} className="mt-10 relative hidden lg:block">
            <div className="relative w-64 border-2 border-ink bg-bone p-2 pb-10 rotate-[-3deg] shadow-[8px_8px_0_#0F0F10]">
              <span className="tape tape-tr" />
              <div className="relative overflow-hidden aspect-[4/3]">
                <img src={IMG.crew} alt="La community Hammer Gym" loading="lazy" className="w-full h-full object-cover photo-warm" />
                <div className="tint-red" />
              </div>
              <span className="absolute bottom-2 left-3 font-hand text-lg">#hammerfamily</span>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-10">
            <p className="font-display text-2xl">{t.trial.or}</p>
            <a
              href={waLink(t.wa.prefill)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trial-whatsapp-link"
              className="btn-brutal btn-wa mt-3"
            >
              <WhatsAppIcon className="w-5 h-5" /> {t.wa.cta}
            </a>
          </Reveal>
        </div>

        {/* form */}
        <div className="lg:col-span-7">
          <Reveal y={30}>
            <div className="relative bg-bone border-[3px] border-ink shadow-[8px_8px_0_#D7192A] sm:shadow-[14px_14px_0_#D7192A] p-5 sm:p-10">
              <div className="absolute -top-5 left-6 bg-ink text-bone font-mono text-[11px] uppercase tracking-[0.22em] px-3 py-2">
                Form / N.05
              </div>
              {done ? (
                <div data-testid="free-trial-success" className="py-14 text-center">
                  <CheckCircle2 className="w-16 h-16 text-hammer-red mx-auto" />
                  <h3 className="font-display text-5xl mt-5">{t.trial.successTitle}</h3>
                  <p className="mt-3 text-ink/70">{t.trial.successText}</p>
                  <button
                    type="button"
                    data-testid="free-trial-again-button"
                    onClick={() => setDone(false)}
                    className="btn-brutal btn-ink mt-8"
                  >
                    {t.trial.again}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="free-trial-form" className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="ft-first" className="font-mono text-[11px] uppercase tracking-[0.18em]">
                      {t.trial.first} *
                    </Label>
                    <Input id="ft-first" data-testid="free-trial-name-input" value={form.first_name} onChange={set("first_name")} className={`${fieldCls} mt-2`} autoComplete="given-name" />
                    <Err k="first_name" />
                  </div>
                  <div>
                    <Label htmlFor="ft-last" className="font-mono text-[11px] uppercase tracking-[0.18em]">
                      {t.trial.last}
                    </Label>
                    <Input id="ft-last" data-testid="free-trial-lastname-input" value={form.last_name} onChange={set("last_name")} className={`${fieldCls} mt-2`} autoComplete="family-name" />
                  </div>
                  <div>
                    <Label htmlFor="ft-phone" className="font-mono text-[11px] uppercase tracking-[0.18em]">
                      {t.trial.phone} *
                    </Label>
                    <Input id="ft-phone" type="tel" inputMode="tel" placeholder="+41 7x xxx xx xx" data-testid="free-trial-phone-input" value={form.phone} onChange={set("phone")} className={`${fieldCls} mt-2`} autoComplete="tel" />
                    <Err k="phone" />
                  </div>
                  <div>
                    <Label htmlFor="ft-email" className="font-mono text-[11px] uppercase tracking-[0.18em]">
                      {t.trial.email}
                    </Label>
                    <Input id="ft-email" type="email" data-testid="free-trial-email-input" value={form.email} onChange={set("email")} className={`${fieldCls} mt-2`} autoComplete="email" />
                    <Err k="email" />
                  </div>
                  <div>
                    <Label className="font-mono text-[11px] uppercase tracking-[0.18em]">{t.trial.goal}</Label>
                    <Select value={form.goal} onValueChange={set("goal")}>
                      <SelectTrigger data-testid="free-trial-goal-select" className={`${fieldCls} mt-2`}>
                        <SelectValue placeholder={t.trial.select} />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-2 border-ink bg-bone">
                        {Object.entries(t.trial.goals).map(([k, v]) => (
                          <SelectItem key={k} value={k} data-testid={`free-trial-goal-${k}`} className="rounded-none focus:bg-ink focus:text-bone">
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-mono text-[11px] uppercase tracking-[0.18em]">{t.trial.time}</Label>
                    <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
                      <SelectTrigger data-testid="free-trial-time-select" className={`${fieldCls} mt-2`}>
                        <SelectValue placeholder={t.trial.select} />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-2 border-ink bg-bone">
                        {Object.entries(t.trial.times).map(([k, v]) => (
                          <SelectItem key={k} value={k} data-testid={`free-trial-time-${k}`} className="rounded-none focus:bg-ink focus:text-bone">
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ft-msg" className="font-mono text-[11px] uppercase tracking-[0.18em]">
                      {t.trial.message}
                    </Label>
                    <Textarea
                      id="ft-msg"
                      data-testid="free-trial-message-input"
                      value={form.message}
                      onChange={set("message")}
                      placeholder={t.trial.messagePh}
                      rows={4}
                      maxLength={1000}
                      className="rounded-none border-2 border-ink bg-bone text-ink placeholder:text-ink/40 focus-visible:ring-0 focus-visible:border-hammer-red shadow-none text-base mt-2"
                    />
                  </div>
                  {/* honeypot */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        data-testid="free-trial-consent-checkbox"
                        checked={form.consent}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v === true }))}
                        className="mt-0.5 h-5 w-5 rounded-none border-2 border-ink data-[state=checked]:bg-hammer-red data-[state=checked]:border-hammer-red"
                      />
                      <span className="text-sm text-ink/80 leading-snug">{t.trial.consent}</span>
                    </label>
                    <Err k="consent" />
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <button type="submit" disabled={loading} data-testid="free-trial-submit-button" className="btn-brutal btn-red w-full sm:w-auto !text-2xl disabled:opacity-60">
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" /> {t.trial.sending}
                        </>
                      ) : (
                        <>
                          {t.trial.submit} <ArrowUpRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
